<script setup lang="ts">
import MainLayout from '@/common/components/MainLayout.vue';
import Tabs from 'primevue/tabs';
import TabList from 'primevue/tablist';
import Tab from 'primevue/tab';
import TabPanels from 'primevue/tabpanels';
import TabPanel from 'primevue/tabpanel';
import { computed, ref, watch } from 'vue';
import { useLanguageStore } from 'shared/translations/stores/languageStore';
import { storeToRefs } from 'pinia';
import { useRoute, useRouter } from 'vue-router';
import ProductsListTab from './components/ProductsListTab.vue';
import ProductGroupsListTab from './groups/components/ProductGroupsListTab.vue';
import MappingProfileListTab from './mapping/components/MappingProfileListTab.vue';
import MappingTraceListTab from './mapping/components/MappingTraceListTab.vue';
import { useFeatureFlagStore } from '@/featureFlags/stores/featureFlagStore';
import { DefinedFeatureFlags } from 'shared/featureFlags/domain/definedFeatureFlags';

type ProductsTab = 'products' | 'productGroups' | 'mappingProfile' | 'mappingTrace';

const languageStore = useLanguageStore();
const { translations } = storeToRefs(languageStore);

const featureFlagStore = useFeatureFlagStore();
const isPackagesEnabled = computed(() =>
  featureFlagStore.isFeatureEnabled(DefinedFeatureFlags.Packages),
);

function normalizeTab(tab: unknown): ProductsTab {
  if (tab === 'productGroups') return 'productGroups';
  if (isPackagesEnabled.value && tab === 'mappingProfile') return 'mappingProfile';
  if (isPackagesEnabled.value && tab === 'mappingTrace') return 'mappingTrace';
  return 'products';
}

const route = useRoute();
const router = useRouter();
const selectedTab = ref<ProductsTab>(normalizeTab(route.query.tab));

const tabs = computed(() => [
  { value: 'products' as const, label: translations.value.generic.products },
  { value: 'productGroups' as const, label: translations.value.generic.groups },
  ...(isPackagesEnabled.value
    ? [
        { value: 'mappingProfile' as const, label: 'Mapping profile' },
        { value: 'mappingTrace' as const, label: 'Mapping trace' },
      ]
    : []),
]);

watch(
  () => route.query.tab,
  (queryTab) => {
    const normalized = normalizeTab(queryTab);
    if (selectedTab.value !== normalized) {
      selectedTab.value = normalized;
    }
  },
);

watch(selectedTab, async (tab) => {
  const current = normalizeTab(route.query.tab);
  if (current === tab) return;

  await router.replace({
    name: 'products',
    query: {
      ...route.query,
      tab,
    },
  });
});
</script>

<template>
  <MainLayout>
    <Tabs v-model:value="selectedTab">
      <TabList class="products-tab-list">
        <Tab v-for="tab in tabs" :key="tab.value" :value="tab.value">{{ tab.label }}</Tab>
      </TabList>
      <TabPanels class="products-tab-panels">
        <TabPanel value="products" class="products-tab-panel">
          <ProductsListTab v-if="selectedTab === 'products'"></ProductsListTab>
        </TabPanel>
        <TabPanel value="productGroups" class="products-tab-panel">
          <ProductGroupsListTab v-if="selectedTab === 'productGroups'"></ProductGroupsListTab>
        </TabPanel>
        <TabPanel value="mappingProfile" class="products-tab-panel">
          <MappingProfileListTab v-if="selectedTab === 'mappingProfile'"></MappingProfileListTab>
        </TabPanel>
        <TabPanel value="mappingTrace" class="products-tab-panel">
          <MappingTraceListTab v-if="selectedTab === 'mappingTrace'"></MappingTraceListTab>
        </TabPanel>
      </TabPanels>
    </Tabs>
  </MainLayout>
</template>

<style>
.p-tabs {
  display: flex;
  flex-direction: column;
  height: 100%;
  min-height: 0;
}

.products-tab-list.p-tablist {
  border-top-left-radius: 12px;
  border-top-right-radius: 12px;
  overflow: hidden;
  flex-shrink: 0;
}

.products-tab-panels.p-tabpanels {
  border-bottom-left-radius: 12px;
  border-bottom-right-radius: 12px;
  overflow: hidden;
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
}

.products-tab-panel.p-tabpanel {
  border-radius: inherit;
  overflow: hidden;
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
}
</style>
